import { Component } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import {
  AlertController,
  LoadingController,
  NavController,
  NavParams,
} from "ionic-angular";
import { ViewController } from "ionic-angular/navigation/view-controller";
import {
  AmendedAction,
  DropdownItem,
  OrderTypeConfiguration,
} from "models/order-management/models";
import { OrderResponse } from "models/order-management/orderResponse";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";
import CompanyResourceSummary from "models/resource-management/company-resource-summary";
import CredentialedResource from "models/resource-management/credentialed-resource";
import { Subject } from "rxjs";

@Component({
  selector: "edit-hospice-coverage",
  templateUrl: "edit-hospice-coverage.html",
})
export default class EditHospiceCoverage {
  editHospiceCoverageForm: FormGroup;
  credentialedResource: CredentialedResource;
  providers: CompanyResourceSummary[] = [];
  providerSearchSubject: Subject<string> = new Subject();
  order: OrderResponse;
  orders: Array<StatefulOrderResponse> = [];
  patientChartId: number = 0;
  spinner: any = null;
  spinnerCounter: number = 0;
  userDisplayName: string;
  newHospicePaysDecisionIs: boolean;
  reason: string;
  coverageDesignationDropdownItems: DropdownItem[] = [];
  orderTypeConfigurations: OrderTypeConfiguration[] = [];
  // CO-3742 (REQ-006): non-medication order types capture a required free-text reason instead
  // of the canned dropdown. isFreeTextReason is true when the order type's coverageDesignation
  // config carries no canned dropdown items.
  isFreeTextReason: boolean = false;
  reasonMaxLength: number = 500;

  public constructor(
    private fb: FormBuilder,
    public navCtrl: NavController,
    public viewCtrl: ViewController,
    public navParams: NavParams,
    private alertController: AlertController,
    private loadingCtrl: LoadingController
  ) {
    this.order = this.navParams.data.order;
    this.editHospiceCoverageForm = new FormGroup({
      hospicePays: this.fb.control(null, Validators.required),
      coverageDesignation: this.fb.control(null),
    });
    // Get the dropdown items for the coverage designation
    this.orderTypeConfigurations = this.navParams.data.orderTypeConfigurations;
    const orderTypeConfiguration = this.orderTypeConfigurations.find(
      (configuration) => configuration.orderType === this.order.orderType
    );
    const coverageDesignationComponent =
      orderTypeConfiguration.components?.find((component) =>
        component.properties.some(
          (property) => property.name === "coverageDesignation"
        )
      );
    this.coverageDesignationDropdownItems =
      coverageDesignationComponent?.properties[0].dropdownItems;

    // CO-3742 (REQ-006): decide free-text vs canned-dropdown from the config and read the
    // configured character cap (Q3 = 500). No dropdown items => free-text reason.
    this.isFreeTextReason =
      !this.coverageDesignationDropdownItems ||
      this.coverageDesignationDropdownItems.length === 0;
    this.reasonMaxLength =
      coverageDesignationComponent?.properties[0].max || 500;

    // AC-006-3/5/6: the reason is required to save only when Hospice Coverage = "No"; on "Yes"
    // it is un-required (and hidden); previously-typed text is preserved across toggles because
    // we never reset the control value.
    this.editHospiceCoverageForm
      .get("coverageDesignation")
      .valueChanges.subscribe(() => {}); // touch to ensure control participates in validity
    this.editHospiceCoverageForm
      .get("hospicePays")
      .valueChanges.subscribe((value: string) =>
        this.applyReasonValidators(value)
      );
  }

  /**
   * CO-3742 (REQ-006): apply/clear the free-text reason validators as Hospice Coverage toggles.
   * Required only on "No"; maxLength always (free-text only). Never resets the value (AC-006-6).
   */
  private applyReasonValidators(hospicePaysValue: string): void {
    const control = this.editHospiceCoverageForm.get("coverageDesignation");
    if (!this.isFreeTextReason) {
      control.clearValidators();
      control.updateValueAndValidity();
      return;
    }
    const validators = [Validators.maxLength(this.reasonMaxLength)];
    if (hospicePaysValue === "no") {
      validators.push(Validators.required);
    }
    control.setValidators(validators);
    control.updateValueAndValidity();
  }

  // AC-006-3/5: show the required marker only for a free-text reason while coverage = "No".
  isReasonRequired(): boolean {
    return this.isFreeTextReason && this.hospicePaysNo;
  }

  // AC-006-3: block save until the form (incl. a required free-text reason) is valid.
  isSubmitDisabled(): boolean {
    return this.editHospiceCoverageForm.invalid;
  }

  /**
   * Spinner Methods
   */

  increaseSpinnerCount(message) {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  /**
   * onClick() Events
   */

  cancel(): void {
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present().then((r) => console.log("Handled"));
  }

  save(): void {
    let amendedAction: AmendedAction = {
      reason: "Change to determination of who will pay for this order.",
      properties: [
        {
          newValue:
            this.editHospiceCoverageForm.controls["hospicePays"].value == "yes",
          previousValue: this.order.hospicePays,
          previousValueDisplay: this.convertBooleanToDisplayValue(
            this.order.hospicePays
          ),
          propertyName: "hospicePays",
        },
      ],
    };

    // Handle coverage designation, if one is specified
    if (
      this.hospicePaysNo &&
      this.editHospiceCoverageForm.controls["coverageDesignation"].value
    ) {
      amendedAction.properties.push({
        newValue:
          this.editHospiceCoverageForm.controls["coverageDesignation"].value,
        previousValue: this.order.coverageDesignation,
        previousValueDisplay: this.getCoverageDesignationLabel(
          this.order.coverageDesignation
        ),
        propertyName: "coverageDesignation",
      });
    }
    // If there was coverage designation and the user changes coverage to Yes, it needs to be removed
    else if (!this.hospicePaysNo && this.order.coverageDesignation) {
      amendedAction.properties.push({
        newValue: "",
        previousValue: this.order.coverageDesignation,
        previousValueDisplay: this.getCoverageDesignationLabel(
          this.order.coverageDesignation
        ),
        propertyName: "coverageDesignation",
      });
    }

    this.viewCtrl.dismiss(amendedAction);
  }

  convertBooleanToDisplayValue(hospicePays: boolean): string | null {
    let amendedDecision: string = null;
    switch (hospicePays) {
      case true:
        amendedDecision = "Yes";
        break;
      default:
        amendedDecision = "No";
    }
    return amendedDecision;
  }

  isHospicePaysSelected() {
    return !this.editHospiceCoverageForm.controls["hospicePays"].value;
  }

  get hospicePaysNo() {
    return this.editHospiceCoverageForm.controls["hospicePays"].value === "no";
  }

  getCoverageDesignationLabel(coverageDesignation: string) {
    const dropdownItem = this.coverageDesignationDropdownItems.find(
      (item) => item.value === coverageDesignation
    );
    if (dropdownItem) {
      return dropdownItem.label;
    }
    return coverageDesignation;
  }
}



// WEBPACK FOOTER //
// ./src/pages/orders-entry-om/edit-hospice-coverage/edit-hospice-coverage.ts